import { PageHeader } from "@ant-design/pro-components";
import { CloseOutlined, UploadOutlined } from "@ant-design/icons";
import { CampaignOutlined } from "@mui/icons-material";
import { Alert, Button, Card, Form, Input, Select, Spin, Tag, Tooltip, message } from "antd";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import jalali from "react-date-object/calendars/jalali";
import persian_fa from "react-date-object/locales/persian_fa";
import DatePicker from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../api/api";
import CheckPermission from "../../../Util/CheckPermission";
import { canAccessSmsCampaign } from "../../../Util/smsCampaignPermissions";
import ConvertNumbersToEn from "../../../Util/ConvertNumbersToEn";
import countSmsSegments from "../../../Util/countSmsSegments";
import readMobileNumbersFile from "../../../Util/readMobileNumbersFile";
import {
  countSmsMessageCharacters,
  smsCampaignMessageBody,
  withSmsCampaignOptOut,
} from "../../../Util/smsCampaignMessage";
import {
  isSmsCampaignEditable,
  campaignStatusColor,
  campaignStatusLabel,
  countRecipientTokens,
} from "../../../Util/smsCampaign";
import styles from "./SmsCampaignEditor.module.css";

const lineLabel = (line) =>
  line.provider_title ? `${line.number} - ${line.provider_title}` : line.number;

const SmsCampaignEditor = ({ canEdit, canView, canList }) => {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const messageText = Form.useWatch("message", form) || "";
  const fullMessage = withSmsCampaignOptOut(messageText);
  const messageCharacters = countSmsMessageCharacters(fullMessage);
  const mobilesText = Form.useWatch("mobiles", form) || "";
  const [lines, setLines] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [sendAt, setSendAt] = useState(null);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [invalidNumbers, setInvalidNumbers] = useState("");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const numbersFileInput = useRef(null);

  const canOpenEditor =
    isNew || (isSmsCampaignEditable(campaign) && canEdit);

  const applyCampaign = useCallback(
    (data) => {
      const nextLines = Array.isArray(data.lines) ? data.lines : [];
      const lineInList = nextLines.some((line) => line.id === data.line_id);
      setLines(nextLines);
      setCampaign(data);
      setSendAt(data.send_at || null);
      form.setFieldsValue({
        title: data.title || "",
        message: withSmsCampaignOptOut(data.message),
        line: lineInList ? data.line_id : undefined,
        mobiles: (data.queues || []).map((queue) => queue.mobile).join("\n"),
      });
    },
    [form]
  );

  const loadDetails = useCallback(async () => {
    setReady(false);
    setError("");
    try {
      const res = await api.get("/smscampaign/details/" + id);
      const data = res.data?.data;
      if (!data) {
        setError("کمپین پیدا نشد.");
        return;
      }
      applyCampaign(data);
    } catch (requestError) {
      setError(
        requestError.response?.data?.error?.message || "خطا در دریافت کمپین"
      );
    } finally {
      setReady(true);
    }
  }, [applyCampaign, id]);

  useEffect(() => {
    if (!isNew) {
      loadDetails();
      return;
    }

    let cancelled = false;
    setReady(false);
    setError("");
    api
      .get("/smscampaign/lines")
      .then((res) => {
        if (cancelled) return;
        const nextLines = Array.isArray(res.data) ? res.data : [];
        setLines(nextLines);
        const preferred =
          nextLines.find((line) => line.isDefault) || nextLines[0];
        if (preferred) form.setFieldsValue({ line: preferred.id });
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(
          requestError.response?.data?.error?.message ||
            "خطا در دریافت خطوط پیامک"
        );
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [form, isNew, loadDetails]);

  useEffect(() => {
    if (!isNew && ready && campaign && !canOpenEditor) {
      navigate("/sms-campaigns/" + id, { replace: true });
    }
  }, [campaign, canOpenEditor, id, isNew, navigate, ready]);

  const importNumbers = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setImporting(true);
    setImportError("");
    try {
      const numbers = await readMobileNumbersFile(file);
      form.setFieldsValue({ mobiles: numbers.join("\n") });
      setInvalidNumbers("");
      message.success(`${numbers.length} شماره از فایل خوانده شد.`);
    } catch (fileError) {
      setImportError(fileError.message || "خواندن فایل انجام نشد.");
    } finally {
      setImporting(false);
    }
  };

  const submit = async (values) => {
    setSaving(true);
    setError("");
    setInvalidNumbers("");
    const sendAtDate = sendAt ? new Date(sendAt) : null;
    sendAtDate?.setSeconds(0, 0);
    const payload = {
      title: values.title.trim(),
      message: withSmsCampaignOptOut(values.message).trim(),
      line: values.line,
      send_at: sendAtDate ? sendAtDate.toISOString() : null,
      mobiles: ConvertNumbersToEn(values.mobiles || ""),
    };
    form.setFieldsValue({ message: withSmsCampaignOptOut(values.message) });

    try {
      if (isNew) {
        const res = await api.post("/smscampaign/create", payload);
        message.success(
          res.data?.recipientCount != null
            ? `کمپین برای ${res.data.recipientCount} شماره ایجاد شد.`
            : "کمپین ایجاد شد."
        );
        navigate(
          canView ? "/sms-campaigns/" + res.data.id : canList ? "/sms-campaigns" : "/"
        );
        return;
      }

      const res = await api.post("/smscampaign/edit/" + id, payload);
      message.success(
        res.data?.recipientCount != null
          ? `کمپین برای ${res.data.recipientCount} شماره ذخیره شد.`
          : "کمپین ذخیره شد."
      );
      navigate("/sms-campaigns/" + id);
    } catch (requestError) {
      let text =
        requestError.response?.data?.error?.message || "ذخیره کمپین انجام نشد.";
      const invalidNumbersPrefix = "شماره نامعتبر:";
      if (text.startsWith(invalidNumbersPrefix)) {
        setInvalidNumbers(text.slice(invalidNumbersPrefix.length).trim());
        text = "شماره‌های نامعتبر را اصلاح کنید.";
      }
      setError(text);
      message.error(text);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title={isNew ? "کمپین پیامک جدید" : "ویرایش کمپین"}
        tags={
          !isNew && campaign ? (
            <Tag color={campaignStatusColor(campaign.status)}>
              {campaignStatusLabel(campaign.status)}
            </Tag>
          ) : null
        }
        avatar={{
          icon: <CampaignOutlined />,
          size: "large",
          style: { paddingTop: "8px" },
        }}
      />

      <Spin spinning={!ready || saving}>
        {error ? (
          <Alert className={styles.formCard} type="error" message={error} />
        ) : null}

        {ready && canOpenEditor ? (
          <Card className={styles.formCard}>
            <Form
              form={form}
              layout="vertical"
              autoComplete="off"
              initialValues={{ message: withSmsCampaignOptOut("") }}
              onFinish={submit}
            >
              <Form.Item
                name="title"
                label="عنوان"
                rules={[
                  { required: true, whitespace: true, message: "عنوان را وارد کنید." },
                  { max: 120, message: "عنوان حداکثر ۱۲۰ کاراکتر است." },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="line"
                label="خط ارسالی"
                rules={[{ required: true, message: "خط ارسالی را انتخاب کنید." }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="خط ارسالی"
                  options={lines.map((line) => ({
                    label: lineLabel(line),
                    value: line.id,
                  }))}
                />
              </Form.Item>
              {campaign?.line_number ? (
                <div className={styles.hint}>
                  خط فعلی: <span dir="ltr">{campaign.line_number}</span>
                </div>
              ) : null}
              {!lines.length ? (
                <div className={styles.hint}>برای نقش شما هیچ خط پیامکی مجاز نشده است.</div>
              ) : null}

              <Form.Item label="زمان ارسال">
                <DatePicker
                  value={sendAt ? new Date(sendAt) : null}
                  onChange={(value) =>
                    setSendAt(value ? value.toDate().toISOString() : null)
                  }
                  format="YYYY/MM/DD HH:mm"
                  calendar={jalali}
                  locale={persian_fa}
                  plugins={[<TimePicker key="time" position="left" hideSeconds />]}
                  render={(value, openCalendar) => (
                    <Input
                      value={value}
                      onClick={openCalendar}
                      readOnly
                      suffix={
                        sendAt ? (
                          <Button
                            type="text"
                            size="small"
                            htmlType="button"
                            aria-label="پاک کردن زمان ارسال"
                            icon={<CloseOutlined style={{ fontSize: 12 }} />}
                            onClick={(event) => {
                              event.stopPropagation();
                              setSendAt(null);
                            }}
                          />
                        ) : null
                      }
                    />
                  )}
                />
                <div className={styles.hint}>
                  اگر زمان خالی باشد، کمپین در اولین فرصت ارسال می‌شود.
                </div>
              </Form.Item>

              <Form.Item
                name="message"
                className={styles.messageField}
                label={
                  <span className={styles.messageLabel}>
                    <span>متن پیام</span>
                    <span className={styles.messageStats} id="message-stats">
                      <Tag color="blue" className={styles.messageStat}>
                        پیامک <b>{countSmsSegments(fullMessage).toLocaleString("fa-IR")}</b>
                      </Tag>
                      <Tag
                        color={messageCharacters.isPersian ? "purple" : "cyan"}
                        className={styles.messageStat}
                        title="تعداد کل کاراکترهای متن پیام"
                      >
                        {messageCharacters.isPersian ? "فارسی" : "انگلیسی"}{" "}
                        <b>{messageCharacters.count.toLocaleString("fa-IR")}</b>
                      </Tag>
                    </span>
                  </span>
                }
                rules={[
                  {
                    required: true,
                    validator: (_, value) =>
                      smsCampaignMessageBody(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error("متن پیام را وارد کنید.")),
                  },
                ]}
              >
                <Input.TextArea
                  aria-label="متن پیام"
                  aria-describedby="message-stats"
                  rows={5}
                  onBlur={() =>
                    form.setFieldsValue({
                      message: withSmsCampaignOptOut(form.getFieldValue("message")),
                    })
                  }
                />
              </Form.Item>

              <input
                ref={numbersFileInput}
                type="file"
                accept=".csv,.xlsx,.xls"
                aria-label="فایل شماره‌ها"
                hidden
                style={{ display: "none" }}
                disabled={importing || saving}
                onChange={importNumbers}
              />
              <div className={styles.mobilesLabel}>
                <label className={styles.mobilesTitle} htmlFor="mobiles">
                  شماره‌ها
                </label>
                <Tooltip
                  title="ستون اولِ اولین شیت خوانده می‌شود. با انتخاب فایل، شماره‌های فعلی جایگزین می‌شوند. سطر عنوان «موبایل» یا «mobile» اختیاری است."
                  trigger={["hover", "focus"]}
                >
                  <Button
                    className={styles.numbersImport}
                    size="small"
                    htmlType="button"
                    icon={<UploadOutlined />}
                    loading={importing}
                    disabled={saving}
                    onClick={() => numbersFileInput.current?.click()}
                  >
                    بارگذاری CSV یا Excel
                  </Button>
                </Tooltip>
              </div>
              <Form.Item
                name="mobiles"
                rules={[{ required: true, message: "شماره‌ها را وارد کنید." }]}
                validateStatus={invalidNumbers ? "error" : undefined}
                extra={
                  invalidNumbers ? (
                    <Alert
                      type="error"
                      message="شماره‌های نامعتبر"
                      description={
                        <div className={styles.invalidNumbers} dir="ltr">
                          {invalidNumbers.split(/،\s*/).map((number, index) => (
                            <div key={index}>{number}</div>
                          ))}
                        </div>
                      }
                    />
                  ) : null
                }
              >
                <Input.TextArea
                  aria-label="شماره‌ها"
                  className={styles.mobiles}
                  rows={8}
                  disabled={importing}
                  placeholder={"هر شماره در یک خط\n09121234567"}
                  onChange={() => setInvalidNumbers("")}
                />
              </Form.Item>
              <div className={styles.hint}>
                {countRecipientTokens(mobilesText)} شماره. شماره‌های تکراری یک‌بار
                ثبت می‌شوند. حداکثر ۱۰۰۰۰ شماره.
              </div>

              {importError ? <Alert type="error" message={importError} showIcon /> : null}

              <Form.Item>
                <div className={styles.actions}>
                  <Button type="primary" htmlType="submit" loading={saving} disabled={importing}>
                    {isNew ? "ایجاد کمپین" : "ذخیره"}
                  </Button>
                  <Button
                    htmlType="button"
                    onClick={() =>
                      navigate(isNew ? (canList ? "/sms-campaigns" : "/") : "/sms-campaigns/" + id)
                    }
                  >
                    بازگشت
                  </Button>
                </div>
              </Form.Item>
            </Form>
          </Card>
        ) : null}
      </Spin>
    </div>
  );
};

const mapStateToProps = (state) => {
  const permissions = state.userData ? state.userData.permissions : [];
  return {
    canEdit: CheckPermission("sms_campaign_edit", permissions),
    canView: canAccessSmsCampaign("view", permissions),
    canList: canAccessSmsCampaign("list", permissions),
  };
};

export default connect(mapStateToProps)(memo(SmsCampaignEditor));
